import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";

export type AttachmentKind = "person" | "deal" | "organization" | "partner";

export interface AutoAttachment {
  kind: AttachmentKind;
  id: string;
  label: string;
}

const ROUTE_PATTERNS: Array<{ regex: RegExp; kind: AttachmentKind }> = [
  { regex: /^\/contacts\/([0-9a-fA-F-]{36})/, kind: "person" },
  { regex: /^\/deals\/([0-9a-fA-F-]{36})/, kind: "deal" },
  { regex: /^\/organizations\/([0-9a-fA-F-]{36})/, kind: "organization" },
  { regex: /^\/partners\/([0-9a-fA-F-]{36})/, kind: "partner" },
];

function detect(pathname: string): { kind: AttachmentKind; id: string } | null {
  for (const { regex, kind } of ROUTE_PATTERNS) {
    const m = pathname.match(regex);
    if (m && m[1]) return { kind, id: m[1] };
  }
  return null;
}

async function fetchLabel(kind: AttachmentKind, id: string): Promise<string> {
  const manna = supabase.schema("manna");
  if (kind === "person") {
    const { data, error } = await manna
      .from("persons_view")
      .select("first_name, last_name, full_name")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return "(unknown person)";
    if (data.full_name) return data.full_name;
    const composed = `${data.first_name ?? ""} ${data.last_name ?? ""}`.trim();
    return composed || "(unnamed person)";
  }
  if (kind === "deal") {
    const { data, error } = await manna.from("deals").select("name").eq("id", id).maybeSingle();
    if (error) throw error;
    return data?.name ?? "(unnamed deal)";
  }
  if (kind === "organization") {
    const { data, error } = await supabase
      .from("manna_organizations_view")
      .select("name")
      .eq("id", id)
      .maybeSingle();
    if (error) throw error;
    return data?.name ?? "(unnamed organization)";
  }
  const { data, error } = await supabase
    .from("admin_partner_companies_view")
    .select("name")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data?.name ?? "(unnamed partner)";
}

export function useAutoAttach(): {
  attachment: AutoAttachment | null;
  dismiss: () => void;
} {
  const { pathname } = useLocation();
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const detected = detect(pathname);
  const key = detected ? `${detected.kind}:${detected.id}` : null;

  const { data: label } = useQuery({
    queryKey: ["auto-attach-label", key],
    queryFn: () => (detected ? fetchLabel(detected.kind, detected.id) : Promise.resolve(null)),
    enabled: Boolean(detected),
    staleTime: 60_000,
  });

  if (!detected || !key) return { attachment: null, dismiss: () => {} };
  if (dismissedKey === key) return { attachment: null, dismiss: () => {} };

  return {
    attachment: { kind: detected.kind, id: detected.id, label: label ?? "Loading…" },
    dismiss: () => setDismissedKey(key),
  };
}
